<template>
    <div class="qi-modal-backdrop" @click.self="$emit('close')">
        <div class="qi-preview-card">
            <div class="qi-preview-toolbar">
                <span class="text-muted small">{{ toolbarLabel }}</span>
                <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="$emit('close')"><i class="bi bi-x-lg"></i></button>
            </div>
            <div class="qi-preview-doc">
                <div class="qi-preview-header">
                    <div v-if="company.logo_url" class="qi-preview-logo"><img :src="company.logo_url" :alt="company.name"></div>
                    <div v-else class="qi-preview-brand" :style="{ background: accent }">{{ (company.name || 'Your Business').toUpperCase() }}</div>
                    <div class="qi-preview-title-block">
                        <div class="qi-preview-doctype">{{ docType }}</div>
                        <div class="font-weight-bold">{{ company.name }}</div>
                        <div class="small font-weight-bold font-italic" v-if="company.companysettings && company.companysettings.document_tagline">{{ company.companysettings.document_tagline }}</div>
                        <div class="text-muted small" v-if="company.companysettings && company.companysettings.document_description">{{ company.companysettings.document_description }}</div>
                        <div class="text-muted small" v-if="companyAddress">{{ companyAddress }}</div>
                        <div class="text-muted small" v-if="company.phone">Mobile: {{ company.phone }}</div>
                    </div>
                </div>
                <hr>
                <div class="d-flex justify-content-between flex-wrap">
                    <div>
                        <div class="qi-preview-label">{{ docType === 'Quote' ? 'Quote For' : 'Bill To' }}</div>
                        <div class="font-weight-bold">{{ customerLabel(customer) }}</div>
                        <div v-if="customerAttnLine(customer)" class="text-muted small">Attn: {{ customerAttnLine(customer) }}</div>
                        <div v-if="customer && customer.phone" class="text-muted small">{{ customer.phone }}</div>
                        <div v-if="customer && customer.full_address" class="text-muted small">{{ customer.full_address }}</div>
                        <div v-if="customer && customer.krapin" class="text-muted small">PIN: {{ customer.krapin }}</div>
                    </div>
                    <table class="qi-preview-meta">
                        <tr><td>Date:</td><td class="font-weight-bold">{{ formatDate(date) }}</td></tr>
                        <tr v-if="expiryDate"><td>{{ docType === 'Quote' ? 'Valid Until:' : 'Due:' }}</td><td class="font-weight-bold">{{ formatDate(expiryDate) }}</td></tr>
                    </table>
                </div>

                <table class="qi-preview-items">
                    <thead>
                        <tr :style="{ background: accent }">
                            <th>Items</th><th class="text-right">Quantity</th><th class="text-right">Price</th><th class="text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in items" :key="item._key">
                            <td>
                                <div class="font-weight-bold">{{ item.description }}</div>
                                <div v-if="item.item_note" class="text-muted small">{{ item.item_note }}</div>
                            </td>
                            <td class="text-right">{{ item.quantity }}</td>
                            <td class="text-right">{{ formatMoney(item.unit_price) }}</td>
                            <td class="text-right">{{ formatMoney(rowTotal(item)) }}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="qi-preview-summary">
                    <div class="d-flex justify-content-between"><span>Subtotal</span><span>{{ formatMoney(totals.subtotal) }}</span></div>
                    <div class="d-flex justify-content-between text-muted" v-if="totals.discount > 0"><span>Discount</span><span>-{{ formatMoney(totals.discount) }}</span></div>
                    <div class="d-flex justify-content-between text-muted" v-if="totals.tax > 0"><span>Tax</span><span>{{ formatMoney(totals.tax) }}</span></div>
                    <div class="d-flex justify-content-between font-weight-bold qi-preview-grand"><span>Grand Total</span><span>{{ formatMoney(totals.total) }}</span></div>
                </div>

                <div v-if="notes || terms" class="qi-preview-notes">
                    <p v-if="notes"><strong>Notes:</strong> {{ notes }}</p>
                    <p v-if="terms"><strong>Terms:</strong> {{ terms }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatMoney, customerLabel, customerAttnLine } from './docHelpers';

export default {
    name: 'DocumentPreviewModal',
    props: {
        docType: { type: String, required: true },
        customer: { type: Object, default: null },
        items: { type: Array, default: () => [] },
        notes: { type: String, default: '' },
        terms: { type: String, default: '' },
        date: { type: String, default: '' },
        expiryDate: { type: String, default: '' },
        totals: { type: Object, required: true },
        currency: { type: String, default: 'KSh' },
        saved: { type: Boolean, default: false },
        // Let a caller preview unsaved edits (e.g. the onboarding branding step)
        // instead of the last-saved company/companysettings from the store.
        companyOverride: { type: Object, default: null },
        accentOverride: { type: String, default: '' },
    },
    computed: {
        toolbarLabel() {
            return this.saved ? 'This is what your customer sees' : 'Preview — not yet saved';
        },
        company() {
            if (this.companyOverride) return this.companyOverride;
            const u = this.$store.getters.userDetails;
            return (u && u.company) || {};
        },
        accent() {
            if (this.accentOverride) return this.accentOverride;
            const cs = this.company.companysettings;
            return (cs && cs.document_accent_color) || '#fd4719';
        },
        companyAddress() {
            return [this.company.building, this.company.street, this.company.address, this.company.town].filter(Boolean).join(', ');
        },
    },
    methods: {
        customerLabel,
        customerAttnLine,
        formatMoney(v) { return formatMoney(v, this.currency); },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
        rowTotal(item) {
            const base = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0) - (Number(item.discount) || 0);
            const taxPerc = item.tax ? Number(item.tax.tax_perc) : 0;
            const taxAmt = taxPerc ? base * (taxPerc / 100) : 0;
            return Math.max(0, base) + taxAmt;
        },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
.qi-modal-backdrop {
    position: fixed; inset: 0; z-index: 1090; background: rgba(0,0,0,.5);
    display: flex; align-items: flex-start; justify-content: center; padding: 24px 16px; overflow-y: auto;
}
.qi-preview-card { background: #fff; border-radius: 10px; width: 100%; max-width: 720px; box-shadow: 0 16px 48px rgba(0,0,0,.3); overflow: hidden; }
.qi-preview-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; background: #f8f9fa; border-bottom: 1px solid #eee; }
.qi-preview-doc { padding: 28px 32px; max-height: 78vh; overflow-y: auto; }
.qi-preview-header { display: flex; justify-content: space-between; align-items: flex-start; }
.qi-preview-brand { color: #fff; font-weight: bold; padding: 8px 14px; font-size: 15px; }
.qi-preview-logo img { max-height: 44px; max-width: 180px; }
.qi-preview-title-block { text-align: right; }
.qi-preview-doctype { font-size: 20px; font-weight: bold; color: #555; letter-spacing: 1px; margin-bottom: 4px; }
.qi-preview-label { font-size: .68rem; text-transform: uppercase; color: #999; letter-spacing: .04em; margin-bottom: 2px; }
.qi-preview-meta td { padding: 1px 0 1px 16px; font-size: .8rem; text-align: right; }
.qi-preview-items { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: .82rem; }
.qi-preview-items th { color: #fff; padding: 7px 8px; text-align: left; font-size: .72rem; }
.qi-preview-items td { padding: 8px; border-bottom: 1px solid #eee; vertical-align: top; }
.qi-preview-summary { max-width: 260px; margin: 10px 0 0 auto; font-size: .85rem; }
.qi-preview-summary > div { padding: 3px 0; }
.qi-preview-grand { border-top: 2px solid #333; padding-top: 6px !important; margin-top: 4px; font-size: 1rem; }
.qi-preview-notes { margin-top: 20px; font-size: .8rem; color: #555; }
.qi-preview-notes p { margin: 0 0 6px; }
</style>
